import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useInventorySource } from './InventorySource';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoney, fmtNumber, fmtDate, fmtMonth } from './format';
import { c, t, sp, r, tbl } from '../../theme';

// The categories the business reports on monthly, in the order and language of
// the inventory value workbook, so the board and the spreadsheet can be read
// side by side without translating. One column per month, latest snapshot in
// each — the same pick the value trend makes, so a month cannot mean one batch
// here and a different one there.
//
// Two groups, and they are NOT the same kind of number.
//
// EXPOSURE lines come from the export's own columns: a part belongs to one when
// its column is non-zero, which is the rule the workbook uses. Each is an
// INDEPENDENT filter — the honest total of its own column — and they are not
// meant to be added together. Three of them are mutually exclusive, but Non
// Stock Aged +45 days overlaps the two aged bands on 255 lines, so summing the
// group would double-count $119,417. Over Max overlaps everything here too, and
// is shown as its own line because it is excess of a different kind: too much
// of something that still moves, not stock that has stopped moving.
//
// COMPOSITION lines are the classification column, which partitions on-hand
// exactly once — those three do sum to the total. Keeping them in a separate
// group with its own subtotal is the point: stacked in one list, as the
// workbook stacks them, nothing on screen says which half a reader may add.
const EXPOSURE_ROWS = [
  { key: 'over_max_value',          label: 'Over Max',                 count: 'over_max_count',          present: 'over_max_present' },
  { key: 'aged_6_12_months_value',  label: 'Aged 6–12 mo',             count: 'aged_6_12_months_count',  present: 'aged_6_12_months_present' },
  { key: 'aged_12_months_value',    label: 'Aged +12 mo',              count: 'aged_12_months_count',    present: 'aged_12_months_present' },
  { key: 'non_stock_45_days_value', label: 'Non Stock Aged +45 days',  count: 'non_stock_45_days_count', present: 'non_stock_45_days_present' },
  { key: 'critical_spare_value',    label: 'Critical Spare +12 mo',    count: 'critical_spare_count',    present: 'critical_spare_present' },
];

const CLASS_ROWS = [
  { key: 'class_stocked_value',        label: 'Stocked',        count: 'class_stocked_count',        present: 'classification_present' },
  { key: 'class_non_stocked_value',    label: 'Non-Stocked',    count: 'class_non_stocked_count',    present: 'classification_present' },
  { key: 'class_critical_spare_value', label: 'Critical Spare', count: 'class_critical_spare_count', present: 'classification_present' },
];

// A column the source file never carried is ABSENT, not zero — exports
// predating the 2026-08-10 release have no Min at all, and a renamed column
// stops matching and imports as NULL. Both sum to 0.00. Across a row of months
// a zero reads as "we cleared it" rather than "we never had it", which is the
// opposite of the truth, so the server sends a per-figure presence flag and an
// absent cell renders as a dash. The total row has no flag: a batch with no
// value in it is a batch we would not have committed.
function isPresent(month, row) {
  if (!row.present) return true;
  return month[row.present] === true;
}

// Down is green on the exposure lines without qualification: every one of them
// is money tied up where it should not be, and less of it is better. It is NOT
// true of All On Hand or of the classification split — a smaller Critical Spare
// holding is not an improvement, it is a different fleet-risk position — so
// those move without a colour and say only which way they went.
function Change({ months, row, judge }) {
  if (months.length < 2) return <span style={s.noChange}>—</span>;
  const curr = months[months.length - 1];
  const prev = months[months.length - 2];
  if (!isPresent(curr, row) || !isPresent(prev, row)) return <span style={s.noChange}>—</span>;

  const diff = Number(curr[row.key] || 0) - Number(prev[row.key] || 0);
  if (Math.abs(diff) < 1) return <span style={s.noChange}>—</span>;

  const colour = judge ? (diff < 0 ? c.successText : c.dangerText) : c.textMuted;
  return (
    <span style={{ ...s.change, color: colour }}>
      {diff < 0 ? '▼' : '▲'} {fmtMoney(Math.abs(diff))}
    </span>
  );
}

function Row({ months, row, judge, total = false }) {
  const latest      = months[months.length - 1];
  const latestTotal = Number(latest?.total_value || 0);
  const latestValue = Number(latest?.[row.key] || 0);
  const pct         = latestTotal ? (latestValue / latestTotal) * 100 : 0;

  return (
    <tr style={s.tr}>
      <td style={total ? s.tdStrong : s.td}>{row.label}</td>
      {months.map((m, i) => {
        const isLatest = i === months.length - 1;
        if (!isPresent(m, row)) {
          return (
            <td key={m.period} style={s.tdR}>
              <span style={s.absent} title="This month's export did not carry the column">—</span>
            </td>
          );
        }
        return (
          <td key={m.period} style={isLatest || total ? s.tdRStrong : s.tdR}>
            {fmtMoney(m[row.key])}
            {/* Part Lines and share, on the newest column only. Both were
                columns of their own when this table showed a single snapshot,
                and dropping them to fit six months would have lost the two
                figures people actually quote. Part Lines, not Distinct Parts:
                this table is read next to the workbook, and the workbook counts
                lines. */}
            {isLatest && (
              <div style={s.cellSub}>
                {fmtNumber(total ? m.item_count : m[row.count])} lines
                {total ? '' : ` · ${pct.toFixed(1)}%`}
              </div>
            )}
          </td>
        );
      })}
      <td style={s.tdR}><Change months={months} row={row} judge={judge} /></td>
    </tr>
  );
}

export default function ValueCategoryTable() {
  const { api, queryKey } = useInventorySource();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKey('inventory-category-trend'),
    queryFn:  () => api.get('/inventory/category-trend', { params: { months: 6 } }),
  });

  if (isLoading) return <LoadingSpinner />;
  if (error)     return <div style={s.err}>{error.message}</div>;

  const months = data?.months || [];
  if (!months.length) return null;

  const latest = months[months.length - 1];

  return (
    <div>
      <div style={s.head}>
        <div style={s.title}>Inventory Value by Category</div>
        <div style={s.sub}>
          The workbook's reporting lines, from the export's own columns — one column per
          month, the last snapshot in each.
        </div>
        {/* Said here rather than left to be inferred from a short table.
            Every snapshot is retained from the day the feed started and no
            earlier month can be reconstructed: the source file is overwritten
            in place each morning, and the prior-month-end block it carries only
            reaches back one month. A reader who does not know that reads six
            missing columns as a bug. */}
        <div style={s.sub}>
          {months.length === 1
            ? 'One month so far — history builds from here, a column a month'
            : `${months.length} months held`}
          {latest?.is_partial
            ? ` · the ${fmtMonth(latest.snapshot_month)} column is mid-month, as of ${fmtDate(latest.snapshot_as_of_date)}`
            : ` · through ${fmtDate(latest?.snapshot_as_of_date)}`}
        </div>
      </div>

      <div style={s.scroll}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Category</th>
              {months.map((m, i) => (
                <th key={m.period} style={i === months.length - 1 ? s.thRLatest : s.thR}>
                  {fmtMonth(m.snapshot_month)}
                  {m.is_partial && <span style={s.partial}> MTD</span>}
                </th>
              ))}
              <th style={s.thR}>Mo. change</th>
            </tr>
          </thead>
          <tbody>
            <Row months={months} row={{ key: 'total_value', label: 'All On Hand' }} total />

            <tr style={s.trGroup}>
              <td style={s.tdGroup} colSpan={months.length + 2}>
                Exposure — each line is its own filter of the same stock, and they overlap.
                Reading down this group is right; adding it up is not.
              </td>
            </tr>
            {EXPOSURE_ROWS.map((row) => (
              <Row key={row.key} months={months} row={row} judge />
            ))}

            <tr style={s.trGroup}>
              <td style={s.tdGroup} colSpan={months.length + 2}>
                Composition — the classification split, which does sum to All On Hand.
              </td>
            </tr>
            {CLASS_ROWS.map((row) => (
              <Row key={row.key} months={months} row={row} />
            ))}

            {/* Obsolete is on the workbook but cannot come from the export.
                Stating why, in the row where the number should be, is the
                point — an omitted line reads as an oversight, and a derived
                one would be wrong: their July pack shows Aged +12 at $401,524
                against Obsolete at $90,823, so it is a judged subset, not a
                relabel. */}
            <tr style={s.trPending}>
              <td style={s.tdPending}>
                Obsolete
                <span style={s.pendingChip}><Lock size={10} /> needs a vendor-return decision</span>
              </td>
              <td colSpan={months.length + 1} style={s.tdPendingNote}>
                Parts idle 12+ months that cannot be returned to vendor. Whether a vendor will
                take a part back is not in the export, so this has to be recorded — either as a
                flag from Fleetio, or as a disposition approved in the portal.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const s = {
  head:   { padding: `${sp[4]} ${sp[5]} ${sp[3]}` },
  title:  { ...t.h3, color: c.navy },
  sub:    { ...t.micro, color: c.textFaint, marginTop: '2px' },
  err:    { ...t.caption, color: c.dangerText, padding: sp[5] },
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { ...tbl.th, whiteSpace: 'nowrap' },
  thR:    { ...tbl.thR, whiteSpace: 'nowrap' },
  thRLatest: { ...tbl.thR, whiteSpace: 'nowrap', color: c.text },
  tr:     { ...tbl.tr },
  td:     { ...tbl.td, whiteSpace: 'nowrap' },
  tdR:    { ...tbl.tdR },
  tdStrong:  { ...tbl.td, whiteSpace: 'nowrap', fontWeight: 700, color: c.text },
  tdRStrong: { ...tbl.tdR, fontWeight: 700, color: c.text },
  cellSub:   { ...t.micro, color: c.textFaint, fontWeight: 400, marginTop: '1px' },
  absent:    { color: c.textFaint },
  partial:   { ...t.micro, color: c.textFaint, fontWeight: 400 },
  change:   { ...t.tabular, fontWeight: 600 },
  noChange: { color: c.textFaint },
  trGroup:  { background: c.surfaceSunk },
  tdGroup:  {
    ...tbl.td, ...t.micro, color: c.textMuted, whiteSpace: 'normal',
    paddingTop: sp[2], paddingBottom: sp[2],
  },
  trPending: { background: c.surfaceSunk },
  tdPending: {
    ...tbl.td, whiteSpace: 'nowrap', color: c.textMuted, fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: sp[2],
  },
  pendingChip: {
    ...t.micro, fontWeight: 400, color: c.textFaint, display: 'inline-flex',
    alignItems: 'center', gap: '4px', border: `1px solid ${c.border}`,
    borderRadius: r.pill, padding: '2px 8px',
  },
  tdPendingNote: { ...tbl.td, ...t.caption, color: c.textMuted, lineHeight: 1.5, whiteSpace: 'normal' },
};
